import { c as _c } from "react/compiler-runtime";
import { useDropLinkPreviewToggleControl } from "@/components/waves/drops/useDropLinkPreviewToggleControl";
import DropPartMarkdownWithPropLogger from "@/components/drops/view/part/DropPartMarkdownWithPropLogger";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import type { ApiDropGroupMention } from "@/generated/models/ApiDropGroupMention";
import type { ApiDropPart } from "@/generated/models/ApiDropPart";
import type { ApiDropReferencedNFT } from "@/generated/models/ApiDropReferencedNFT";
import type { ApiMentionedWave } from "@/generated/models/ApiMentionedWave";
import type { ApiWaveMin } from "@/generated/models/ApiWaveMin";
import React from "react";
import QuorumProposalCompactContent from "@/components/waves/quorum/QuorumProposalCompactContent";
import { parseQuorumProposalMarkdown } from "@/components/waves/quorum/quorumProposalMarkdown";
import type { DropContentPresentation } from "./dropContentPresentation";
import EditDropLexical from "./EditDropLexical";
import WaveDropQuoteWithDropId from "./WaveDropQuoteWithDropId";
interface WaveDropPartContentMarkdownProps {
  readonly mentionedUsers: Array<ApiDropMentionedUser>;
  readonly mentionedGroups?: Array<ApiDropGroupMention> | undefined;
  readonly mentionedWaves: Array<ApiMentionedWave>;
  readonly referencedNfts: Array<ApiDropReferencedNFT>;
  readonly part: ApiDropPart;
  readonly wave: ApiWaveMin;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isEditing?: boolean | undefined;
  readonly isSaving?: boolean | undefined;
  readonly onSave?: ((newContent: string, mentions?: ApiDropMentionedUser[], mentionedGroups?: ApiDropGroupMention[], mentionedWaves?: ApiMentionedWave[]) => void) | undefined;
  readonly onCancel?: (() => void) | undefined;
  readonly drop?: ApiDrop | undefined; // Add drop to check for edited status
  readonly onLinkCardActionsActiveChange?: ((href: string, active: boolean) => void) | undefined;
  readonly contentPresentation?: DropContentPresentation | undefined;
}
const WaveDropPartContentMarkdown: React.FC<WaveDropPartContentMarkdownProps> = t0 => {
  const $ = _c(51);
  const {
    mentionedUsers,
    mentionedGroups: t1,
    mentionedWaves,
    referencedNfts,
    part,
    wave,
    onQuoteClick,
    isEditing: t2,
    isSaving: t3,
    onSave,
    onCancel,
    drop,
    onLinkCardActionsActiveChange,
    contentPresentation: t4
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const mentionedGroups = t5;
  const isEditing = t2 === undefined ? false : t2;
  const isSaving = t3 === undefined ? false : t3;
  const contentPresentation = t4 === undefined ? "default" : t4;
  const linkPreviewToggleControl = useDropLinkPreviewToggleControl(drop);
  let t6;
  if ($[2] !== drop || $[3] !== wave) {
    t6 = drop?.serial_no === undefined ? [] : [`${wave.id}:${drop.serial_no}`];
    $[2] = drop;
    $[3] = wave;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const currentQuotePath = t6;
  let t7;
  if ($[5] !== contentPresentation || $[6] !== part.content) {
    t7 = contentPresentation === "quorumCompact" ? parseQuorumProposalMarkdown(part.content) : null;
    $[5] = contentPresentation;
    $[6] = part.content;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const compactProposal = t7;
  if (isEditing) {
    const t8 = part.content ?? "";
    const t9 = drop?.wave.authenticated_user_admin === true;
    let t10;
    if ($[8] !== onSave) {
      t10 = (content, mentions, groups, waves) => {
        if (onSave) {
          onSave(content, mentions, groups, waves);
        }
      };
      $[8] = onSave;
      $[9] = t10;
    } else {
      t10 = $[9];
    }
    let t11;
    if ($[10] !== onCancel) {
      t11 = () => {
        if (onCancel) {
          onCancel();
        }
      };
      $[10] = onCancel;
      $[11] = t11;
    } else {
      t11 = $[11];
    }
    let t12;
    if ($[12] !== isSaving || $[13] !== mentionedGroups || $[14] !== mentionedUsers || $[15] !== mentionedWaves || $[16] !== t10 || $[17] !== t11 || $[18] !== t8 || $[19] !== t9 || $[20] !== wave.id) {
      t12 = <EditDropLexical initialContent={t8} initialMentions={mentionedUsers} initialGroupMentions={mentionedGroups} initialWaveMentions={mentionedWaves} canMentionAll={t9} waveId={wave.id} isSaving={isSaving} onSave={t10} onCancel={t11} />;
      $[12] = isSaving;
      $[13] = mentionedGroups;
      $[14] = mentionedUsers;
      $[15] = mentionedWaves;
      $[16] = t10;
      $[17] = t11;
      $[18] = t8;
      $[19] = t9;
      $[20] = wave.id;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    return t12;
  }
  let t8;
  if ($[22] !== compactProposal || $[23] !== currentQuotePath || $[24] !== drop?.hide_link_preview || $[25] !== drop?.id || $[26] !== drop?.nft_links || $[27] !== linkPreviewToggleControl || $[28] !== mentionedGroups || $[29] !== mentionedUsers || $[30] !== mentionedWaves || $[31] !== onLinkCardActionsActiveChange || $[32] !== onQuoteClick || $[33] !== part.content || $[34] !== referencedNfts) {
    t8 = compactProposal ? <QuorumProposalCompactContent proposal={compactProposal} mentionedUsers={mentionedUsers} mentionedGroups={mentionedGroups} mentionedWaves={mentionedWaves} referencedNfts={referencedNfts} nftLinks={drop?.nft_links} onQuoteClick={onQuoteClick} currentDropId={drop?.id} hideLinkPreviews={drop?.hide_link_preview} quotePath={currentQuotePath} linkPreviewToggleControl={linkPreviewToggleControl} onLinkCardActionsActiveChange={onLinkCardActionsActiveChange} /> : <DropPartMarkdownWithPropLogger mentionedUsers={mentionedUsers} mentionedGroups={mentionedGroups} mentionedWaves={mentionedWaves} referencedNfts={referencedNfts} nftLinks={drop?.nft_links} partContent={part.content} onQuoteClick={onQuoteClick} currentDropId={drop?.id} hideLinkPreviews={drop?.hide_link_preview} quotePath={currentQuotePath} linkPreviewToggleControl={linkPreviewToggleControl} onLinkCardActionsActiveChange={onLinkCardActionsActiveChange} />;
    $[22] = compactProposal;
    $[23] = currentQuotePath;
    $[24] = drop?.hide_link_preview;
    $[25] = drop?.id;
    $[26] = drop?.nft_links;
    $[27] = linkPreviewToggleControl;
    $[28] = mentionedGroups;
    $[29] = mentionedUsers;
    $[30] = mentionedWaves;
    $[31] = onLinkCardActionsActiveChange;
    $[32] = onQuoteClick;
    $[33] = part.content;
    $[34] = referencedNfts;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== drop) {
    t9 = typeof drop?.updated_at === "number" && drop.updated_at !== drop.created_at && <div className="tw-mt-0.5 tw-text-[10px] tw-font-normal tw-leading-none tw-text-iron-500">(edited)</div>;
    $[36] = drop;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== t8 || $[39] !== t9) {
    t10 = <div>{t8}{t9}</div>;
    $[38] = t8;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== currentQuotePath || $[42] !== drop || $[43] !== onLinkCardActionsActiveChange || $[44] !== onQuoteClick || $[45] !== part.quoted_drop || $[46] !== wave) {
    t11 = part.quoted_drop?.drop_id && <div className="tw-mt-1.5"><WaveDropQuoteWithDropId dropId={part.quoted_drop.drop_id} partId={part.quoted_drop.drop_part_id} maybeDrop={part.quoted_drop.drop ? {
        ...part.quoted_drop.drop,
        wave
      } : null} onQuoteClick={onQuoteClick} embedPath={drop?.id ? [drop.id] : []} quotePath={currentQuotePath} embedDepth={1} onLinkCardActionsActiveChange={onLinkCardActionsActiveChange} /></div>;
    $[41] = currentQuotePath;
    $[42] = drop;
    $[43] = onLinkCardActionsActiveChange;
    $[44] = onQuoteClick;
    $[45] = part.quoted_drop;
    $[46] = wave;
    $[47] = t11;
  } else {
    t11 = $[47];
  }
  let t12;
  if ($[48] !== t10 || $[49] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[48] = t10;
    $[49] = t11;
    $[50] = t12;
  } else {
    t12 = $[50];
  }
  return t12;
};
export default WaveDropPartContentMarkdown;